// Lancar batida de ponto no ponto de OUTRA pessoa (admin/gerente) — [DA-690].
//
// Nasceu do pedido dele em 08/09/2026: *"preciso de um recurso que me permita fechar o ponto do
// usuario. A Lanna foi embora e esqueceu a sessao aberta, agora os chamados podem cair pra ela"*.
//
// A tela nao pergunta "que hora e agora?" — ela SUGERE a hora e mostra as ancoras que sustentam
// aquela sugestao, porque quem grava aqui esta afirmando uma jornada que nao viu acontecer. A
// sugestao pra fechar um ponto aberto e' o FIM DO EXPEDIENTE da pessoa: e' exatamente o instante
// que o scheduler das 23h gravaria sozinho, entao o botao ANTECIPA em vez de inventar.
import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { EVENTO_PONTO_ALTERADO } from '../../components/AlertaPontoAbertoBanner';

interface EventoCtx {
  id: number;
  tipo: 'entrada' | 'saida';
  hora: string;
  bypass: boolean;
  auto: boolean;
  lancada_por_terceiro: boolean;
}

interface Contexto {
  usuario: { id: number; nome: string; controle_acesso_ativo: boolean };
  data: string;
  eventos: EventoCtx[];
  em_aberto: boolean;
  proximo_tipo: 'entrada' | 'saida';
  horario: { inicio: string | null; fim: string | null; sabado: boolean };
  hora_sugerida: string | null;
  ancoras: {
    primeira_batida: string | null;
    ultima_batida: string | null;
    ultimo_login: string | null;
    ultima_mensagem: string | null;
  };
}

interface Props {
  aberto: boolean;
  usuarioId: number | null;
  usuarioNome: string;
  /** 'YYYY-MM-DD'. Ausente = hoje. */
  data?: string;
  onFechar: () => void;
  /** Chamado depois de gravar, pra quem hospeda recarregar a propria lista. */
  onGravado: () => void;
}

const AMBAR = '#fde68a';
const AMBAR_BORDA = '#f59e0b';

function dataLegivel(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

// "07:18 → 11:00 · 13:00 → (em aberto)" — mesma leitura do `fmtSequenciaPonto` das batidas.
function sequencia(eventos: EventoCtx[]): string {
  const partes: string[] = [];
  for (let k = 0; k < eventos.length; k++) {
    const e = eventos[k]!;
    if (e.tipo === 'entrada') {
      const prox = eventos[k + 1];
      if (prox && prox.tipo === 'saida') {
        partes.push(`${e.hora} → ${prox.hora}${prox.auto ? ' (auto)' : ''}`);
        k++;
      } else {
        partes.push(`${e.hora} → (em aberto)`);
      }
    } else {
      partes.push(`(?) → ${e.hora}${e.auto ? ' (auto)' : ''}`);
    }
  }
  return partes.join('  ·  ');
}

export function ModalLancarBatidaPonto({ aberto, usuarioId, usuarioNome, data, onFechar, onGravado }: Props) {
  const toast = useToast();
  const [ctx, setCtx] = useState<Contexto | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [hora, setHora] = useState('');
  const [motivo, setMotivo] = useState('');
  const [tipo, setTipo] = useState<'entrada' | 'saida'>('saida');
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto || !usuarioId) return;
    let vivo = true;
    setCarregando(true);
    setCtx(null);
    setErro(null);
    const qs = `usuario_id=${usuarioId}${data ? `&data=${data}` : ''}`;
    api.get<Contexto>(`/api/ponto/lancar/contexto?${qs}`)
      .then((r) => {
        if (!vivo) return;
        setCtx(r);
        setTipo(r.proximo_tipo);
        setHora(r.hora_sugerida ?? '');
        setMotivo(r.em_aberto ? 'Saiu e esqueceu de bater a saida' : 'Esqueceu de bater a entrada');
      })
      .catch((e: any) => { if (vivo) setErro(e?.corpo?.erro || e?.message || 'Nao consegui ler o ponto desta pessoa.'); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [aberto, usuarioId, data]);

  async function gravar() {
    if (!ctx || !usuarioId) return;
    setGravando(true);
    setErro(null);
    try {
      const r = await api.post<{ evento: { hora: string }; chamados_devolvidos: number | null }>(
        '/api/ponto/eventos',
        { usuario_id: usuarioId, data: ctx.data, tipo, hora: hora.trim(), motivo: motivo.trim() },
      );
      const devolvidos = r.chamados_devolvidos;
      const extra = devolvidos && devolvidos > 0
        ? ` · ${devolvidos} chamado${devolvidos === 1 ? '' : 's'} voltou pra fila`
        : '';
      toast.sucesso(`${tipo === 'saida' ? 'Saida' : 'Entrada'} de ${ctx.usuario.nome} lancada as ${r.evento.hora}${extra}`);
      onGravado();
      // A faixa "PONTO ABERTO" vive no Layout e tem carregamento proprio (poll de 5 min), entao
      // o `onGravado` de quem hospeda este modal nao a alcanca. Sem este aviso ela seguiria
      // afirmando por minutos o que o toast acabou de negar ([DA-684]).
      window.dispatchEvent(new CustomEvent(EVENTO_PONTO_ALTERADO));
      onFechar();
    } catch (e: any) {
      // Fica NA TELA ate a proxima tentativa: aviso que some em 4s consome a atencao e nao
      // entrega a informacao, e aqui o texto costuma dizer exatamente qual hora nao cabe.
      setErro(e?.corpo?.erro || e?.message || 'Nao consegui gravar.');
    } finally {
      setGravando(false);
    }
  }

  const motivoCurto = motivo.trim().length < 3;
  const semHora = !/^\d{2}:\d{2}$/.test(hora.trim());
  const bloqueado = !ctx || carregando || gravando || motivoCurto || semHora;
  const porQueBloqueado = semHora
    ? 'Informe a hora no formato HH:MM (ex.: 11:00).'
    : motivoCurto
      ? 'Escreva o motivo (pelo menos 3 letras) — ele fica gravado com o seu nome.'
      : null;

  const linha = (rotulo: string, valor: string | null) =>
    valor ? (
      <div>
        {rotulo} · <b style={{ color: 'inherit' }}>{valor}</b>
      </div>
    ) : null;

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo={`${ctx?.em_aberto ? 'Fechar ponto' : 'Lancar batida'} — ${ctx?.usuario.nome ?? usuarioNome}`}
      largura={560}
      compacto
      rodape={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          {porQueBloqueado && (
            <span style={{ fontSize: 12, color: '#b45309', flex: 1 }}>{porQueBloqueado}</span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
            <button
              type="button"
              onClick={() => void gravar()}
              disabled={bloqueado}
              style={{
                padding: '6px 16px', borderRadius: 6, fontWeight: 700, fontSize: 13,
                border: `1px solid ${bloqueado ? 'var(--input-border, #d1d5db)' : AMBAR_BORDA}`,
                background: bloqueado ? 'transparent' : AMBAR,
                color: bloqueado ? 'var(--text-muted)' : '#000',
                cursor: bloqueado ? 'not-allowed' : 'pointer',
              }}
            >
              {gravando ? 'Gravando...' : `Lancar ${tipo} as ${/^\d{2}:\d{2}$/.test(hora.trim()) ? hora.trim() : '--:--'}`}
            </button>
          </div>
        </div>
      }
    >
      {carregando && <div style={{ padding: 20, color: 'var(--text-muted)' }}>Carregando o ponto...</div>}

      {ctx && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>

          <div style={{
            background: 'var(--card-bg, #f9fafb)', border: '1px solid var(--border, rgba(0,0,0,0.10))',
            borderRadius: 7, padding: '9px 11px',
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
              Ponto de {dataLegivel(ctx.data)}
              {ctx.horario.inicio && ctx.horario.fim && ` · horario ${ctx.horario.inicio}–${ctx.horario.fim}`}
            </div>
            <div style={{ fontFamily: 'ui-monospace, monospace' }}>
              {ctx.eventos.length === 0 ? (
                <span style={{ color: 'var(--text-muted)' }}>sem batida nenhuma neste dia</span>
              ) : (
                <>
                  {sequencia(ctx.eventos).replace(' → (em aberto)', '')}
                  {ctx.em_aberto && (
                    <>
                      {' → '}
                      <b style={{ color: '#b45309' }}>(em aberto)</b>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 150 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 3 }}>
                Hora da {tipo}
              </label>
              <input
                value={hora}
                onChange={(e) => setHora(e.target.value)}
                placeholder="HH:MM"
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6,
                  border: '1px solid var(--input-border, #d1d5db)',
                  background: 'var(--input-bg, #fff)', color: 'inherit',
                  fontFamily: 'ui-monospace, monospace', fontSize: 15, fontWeight: 700,
                }}
              />
              {ctx.em_aberto && ctx.horario.fim && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                  fim do horario {ctx.horario.sabado ? 'de sabado ' : ''}dela
                </div>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>Referencias pra conferir</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {linha('entrada batida', ctx.ancoras.primeira_batida)}
                {linha('ultimo login da conta', ctx.ancoras.ultimo_login)}
                {linha('ultima acao da conta no sistema', ctx.ancoras.ultima_mensagem)}
                {!ctx.ancoras.primeira_batida && !ctx.ancoras.ultimo_login && !ctx.ancoras.ultima_mensagem && (
                  <div>nenhum rastro deste dia</div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 3 }}>
              Motivo <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(fica gravado com o seu nome)</span>
            </label>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              style={{
                width: '100%', padding: '6px 8px', borderRadius: 6,
                border: '1px solid var(--input-border, #d1d5db)',
                background: 'var(--input-bg, #fff)', color: 'inherit', fontSize: 13,
              }}
            />
          </div>

          <div style={{ background: AMBAR, color: '#000', borderRadius: 6, padding: '8px 11px', fontSize: 11.5, lineHeight: 1.5 }}>
            <b>Ao gravar:</b>{' '}
            {tipo === 'saida' ? (
              <>
                {ctx.usuario.nome} sai do rodizio na hora e os chamados que ela nunca respondeu voltam pra fila.
                Se estiver com o sistema aberto em algum aparelho, a tela dela passa a pedir ponto.
              </>
            ) : (
              <>
                {ctx.usuario.nome} volta a receber chamados e a fila em espera e' distribuida.
              </>
            )}
            {' '}A batida entra marcada como <b>manual</b> e fica fora do calculo de atraso.
          </div>

          {erro && (
            <div style={{
              background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
              borderRadius: 6, padding: '8px 11px', fontSize: 12,
            }}>
              {erro}
            </div>
          )}
        </div>
      )}

      {!ctx && !carregando && erro && (
        <div style={{ padding: 16, color: '#991b1b', fontSize: 13 }}>{erro}</div>
      )}
    </Modal>
  );
}
